import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin } from 'antd';
import {
  LayoutSubSidebar,
  LayoutSubHeader,
  LayoutBreadcrumbs,
} from '@/features/layout';
import {
  PatientSidebar,
  PatientDetailSubHeader,
  StudyHistoryTimeline,
  usePatientStudies,
} from '@/features/patient-detail';
import type { DateRange, StudyType } from '@/features/patient-detail';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useGetPatientQuery } from '@/state/slices/patients';
import { usePageLoading } from '@/hooks/usePageLoading';
import styles from './PatientDetail.module.css';

export default function PatientDetail() {
  const { patientId } = useParams<{ patientId: string }>();
  const { data: patient, isLoading } = useGetPatientQuery(patientId!, {
    skip: !patientId,
  });

  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [studyType, setStudyType] = useState<StudyType>('all');
  const [showReportHistory, setShowReportHistory] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<
    [string, string] | null
  >(null);

  const handleDateRangeChange = useCallback((value: DateRange) => {
    setDateRange(value);
    if (value !== 'custom') {
      setCustomDateRange(null);
    }
  }, []);

  const handleCustomDateRangeChange = useCallback(
    (value: [string, string] | null) => {
      setCustomDateRange(value);
      if (value === null) {
        setDateRange('all');
      }
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setDateRange('all');
    setStudyType('all');
    setCustomDateRange(null);
  }, []);

  const { studies, isLoading: studiesLoading } = usePatientStudies(
    patientId,
    dateRange,
    studyType,
    customDateRange
  );

  const isEmpty = !studiesLoading && studies.length === 0;

  const breadcrumbs = useMemo(
    () => [
      { label: 'Patients', to: '/patients' },
      {
        label: patient
          ? [patient.firstName, patient.lastName].filter(Boolean).join(' ') ||
            'Patient'
          : 'Patient',
      },
    ],
    [patient]
  );

  usePageLoading(isLoading || studiesLoading);

  return (
    <>
      <LayoutBreadcrumbs items={breadcrumbs} />

      <LayoutSubSidebar title="Patient info">
        {isLoading && (
          <div className={styles.loading}>
            <Spin />
          </div>
        )}
        {patient && <PatientSidebar patient={patient} />}
      </LayoutSubSidebar>

      <LayoutSubHeader>
        <PatientDetailSubHeader
          studyCount={studies.length}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          studyType={studyType}
          onStudyTypeChange={setStudyType}
          showReportHistory={showReportHistory}
          onShowReportHistoryChange={setShowReportHistory}
          customDateRange={customDateRange}
          onCustomDateRangeChange={handleCustomDateRangeChange}
          onResetFilters={handleResetFilters}
        />
      </LayoutSubHeader>

      {isEmpty ? (
        <EmptyState
          headline="No studies found"
          subtext="Try adjusting your filters to see more results."
        />
      ) : (
        <div className={styles.content}>
          <StudyHistoryTimeline
            studies={studies}
            showReportHistory={showReportHistory}
          />
        </div>
      )}
    </>
  );
}
